import { Button } from '@src/components/Button';
import { ErrorHandler } from '@src/components/ErrorHandler';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Table } from './components/Table';
import { Styles } from './includes/styles';

/**
 * Results
 *
 * Displays the search results in a table.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function Results(props) {
  const { handleReset, rows, total } = props;

  const [showResults, setShowResults] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  /**
   * Show warning message if total records exceed 20.
   */
  useEffect(() => {
    if (total >= 20) {
      setShowResults(false);
      setShowWarning(true);
    } else {
      setShowResults(true);
    }
  }, [total]);

  return (
    <ErrorHandler location="src/routes/private/Search/components/Results/Results.jsx">
      <Styles>
        <motion.div
          animate={rows ? 'open' : 'closed'}
          initial={{ opacity: 0 }}
          variants={{
            closed: { opacity: 0 },
            open: { opacity: 1 },
          }}
        >
          <Heading css={{ marginBottom: '$sm' }} offset={3}>
            Search Results
          </Heading>

          {isEmpty(rows) ? (
            <Alert title="Nothing could be found with provided search criteria." variant="info">
              Please refine your search criteria and try again.
            </Alert>
          ) : (
            <React.Fragment>
              {total >= 20 && showWarning && !showResults ? (
                <Alert
                  isVisible={showWarning}
                  onClose={() => {
                    showWarning(false);
                    setShowResults(false);
                  }}
                  variant="warning"
                >
                  <Layout.Stack alignItems="left">
                    <Text fontWeight="bold">
                      Your search exceeded the maximum number of records that we can display (20) and does not include
                      all records.
                      <br />
                      To refine your results, please return to the search screen and add search criteria.
                    </Text>
                    <Layout.Group>
                      <Button
                        onClick={() => {
                          setShowWarning(false);
                          setShowResults(true);
                        }}
                        variant="outline"
                      >
                        Continue to Results
                      </Button>
                      <Button
                        onClick={() => {
                          setShowResults(false);
                          setShowWarning(false);
                          handleReset(true);
                        }}
                        variant="outline"
                      >
                        Refine Search
                      </Button>
                    </Layout.Group>
                  </Layout.Stack>
                </Alert>
              ) : (
                <React.Fragment>{showResults && <Table dataKey="searchResults" rows={rows} />}</React.Fragment>
              )}
            </React.Fragment>
          )}
        </motion.div>
      </Styles>
    </ErrorHandler>
  );
}

Results.propTypes = {
  handleReset: PropTypes.func,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      altMemberId: PropTypes.arrayOf(PropTypes.string),
      cagId: PropTypes.arrayOf(
        PropTypes.shape({
          groupId: PropTypes.string,
          memberId: PropTypes.string,
        })
      ),
      dateRanges: PropTypes.arrayOf(
        PropTypes.shape({
          endDate: PropTypes.string,
          startDate: PropTypes.string,
        })
      ),
      dob: PropTypes.string,
      isConfCommAddress: PropTypes.bool,
      memberName: PropTypes.string,
      primaryAddress: PropTypes.shape({
        address: PropTypes.shape({
          city: PropTypes.string,
          countryCode: PropTypes.string,
          line1: PropTypes.string,
          line2: PropTypes.string,
          line3: PropTypes.string,
          postalCode: PropTypes.string,
          stateOrRegion: PropTypes.string,
        }),
      }),
    })
  ),
  total: PropTypes.number,
};

Results.defaultProps = {
  handleReset: () => {},
  rows: [],
  total: 0,
};
